// ModernLayerPanel — Modern Layout のレイヤーパネル (§9.2 最小版 / §9.6 全面新規).
//
// 方針 (docs/_wip/modern-layout.md §9.6 / §9.2、consult 2026-06-08):
//   - **右ドックに常設** (Classic の floating `LayerPopup` を廃止 → floating 問題解消)。
//   - 操作: アクティブ層選択 + 表示/非表示トグル (= ローカル、同期なし、§9.2)。
//     CSP/PS 風縦積み (目玉アイコン + 行ハイライト)。リスト上端が最前面 (id 降順)。
//   - 追加/削除/並べ替え (≤5、構造変更 = 権限ゲート) は **既存プロトコル範囲では未提供**
//     (whiteboard.layer-add 等のイベントが存在しない)。新規プロトコルイベントは
//     作らない確定スコープなので、本スライスでは **select + visibility のみ** を実装し、
//     構造変更 UI は後続スライス (= protocol 追加とセット) に送る。
//     → これは「engine + 既存 layer protocol を使う / 新規イベントを作らない」指示と、
//       「≤5 追加/削除/並べ替え」要望の競合点。report で consult 論点として明示する。
//   - サムネ/表示濃度 (ローカル opacity)/ドラッグ並べ替えは段階拡張 (今回は最小)。
//
// engine 駆動: 表示/非表示は engine.setLayerVisible (ローカル、broadcast しない、§9.2)。
// アクティブ選択は pen.layerId を書き戻す。layer 行モデルは Classic と同じ pure helper
// (`layerPopup.ts` の buildLayerRows) を UI 非依存ロジックとして再利用する。
//
// ## 見出し (<header>) の撤去 (2026-08-19)
//
// `<header>レイヤー</header>` を撤去した (ユーザー判断 2026-08-18、`ModernBrushPanel` の
// ヘッダ撤去 (307f54e) を他パネルへ広げる作業の一環)。中身 (レイヤー行 + 背景疑似行) を
// 見ればパネルの用途は自明で、見出しは情報を足していない。先行事例は `ModernColorPanel`
// (最初から見出し無し) と `ModernBrushPanel` (307f54e で撤去済み)。パネル名自体は失われ
// ない: section 要素の `aria-label="レイヤー"` (支援技術) / `ModernPanelDragShell` のグリップ帯の
// `aria-label`/`title` (マウス hover) / `ModernPanelVisibilityMenu` (パネル名一覧) が引き
// 続き名前を提供する。
//
// 見出しが持っていた `border-b` (見出しテキストとリストの区切り線) も一緒に撤去した:
// この罫線は「見出しとリストの区切り」としてのみ存在しており、見出しが無くなった今は
// パネル最上端の飾りとして残す積極的な理由がない (他パネルの上端にも同種の罫線は無い)。
// 代わりに `<ul>` 自身の padding を `p-1` → `p-2` に上げ、見出しが担っていた上端の余白を
// 肩代わりさせた (= 他のヘッダ無しパネル (`ModernColorPanel`/`ModernBrushPanel`/
// `ModernPreviewPanel`/`ModernColorPalette`) が section 側で `p-2` を持つのに揃える)。

import type { RegionLayerRoles } from '../../../application/regionMoveFlow.js';
import { buildLayerRows } from '../classic/layerPopup.js';
import { LayerOpacitySlider, shouldSelectOnRowClick } from '../shared/LayerOpacitySlider.js';
import { LayerThumb } from '../shared/LayerThumb.js';
import type { LayerOpacityControl } from '../shared/layerOpacity.js';
import type { LayerThumbnailHub } from '../shared/layerThumbnailHub.js';
import { RegionRoleTag } from '../shared/RegionRoleTag.js';
import { EyeIcon, EyeOffIcon } from './ModernIcons.js';

/** rgb24 → CSS hex (Classic LayerPopup / PalettePanel の toCss と同一). */
const rgbToCss = (rgb: number): string => `#${(rgb & 0xffffff).toString(16).padStart(6, '0')}`;

export interface ModernLayerPanelProps {
  /** 部屋のレイヤー数 (= CLASSIC_LAYER_COUNT 既定の 3。将来 1..5)。 */
  readonly layerCount?: number;
  /** アクティブ層 id (= pen.layerId)。 */
  readonly activeLayerId: number;
  /** 表示状態マップ (id → visible)。 */
  readonly visibility: Record<number, boolean>;
  /** アクティブ層選択。 */
  readonly onSelectActive: (layerId: number) => void;
  /** 表示/非表示トグル (engine.setLayerVisible 経由)。 */
  readonly onToggleVisible: (layerId: number, visible: boolean) => void;
  /** レイヤー表示濃度 (自分の画面だけ・永続しない)。index = layerId、未指定は 100%。 */
  readonly opacities?: readonly number[];
  /** うすさスライダの操作窓口 (engine 未準備は null)。 */
  readonly opacityControl?: LayerOpacityControl | null;
  /**
   * canvas 背景 (= 下地色) の表示状態 (2026-06-20 canvas-background-color §5/B)。
   * 背景は layer ではない独立 property なので `visibility` (layer 用 map) と分離。
   * engine state は部屋に 1 つ・レイアウト非依存 (Classic LayerPopup と挙動一致)。
   */
  readonly backgroundVisible: boolean;
  /** 背景の 👁 トグル (CSS 方式: React state 切替のみ、engine 非関与、broadcast しない)。 */
  readonly onToggleBackgroundVisible: (visible: boolean) => void;
  /**
   * 現在の canvas 背景 *色* (rgb24)。A-4 ■ swatch (canvas-background-color §4.2)。
   * ■ chip の表示色 + (オーナーが開いた) ColorWheelPalette の seed 値。挙動は Classic
   * LayerPopup と一致 (engine state は部屋に 1 つ・レイアウト非依存)。
   */
  readonly backgroundRgb: number;
  /**
   * 背景色変更の権限 (§4.2)。`hasPerm(Perm.CLEAR)` 単独 (room.clearOpen 非依存)。
   * true でのみ ■ が押せる。false は ■ グレーアウト + tooltip。
   */
  readonly canSetBackground: boolean;
  /**
   * ■ swatch click (= 背景色 ColorWheelPalette の開閉トグル)。open-state +
   * ColorWheelPalette のレンダリングは layout 側が所有する (Classic の draw 色
   * `colorWheelOpen` と同流儀)。本パネルは pure component を保つ (web vitest は
   * node 環境で関数直呼びするため hooks を持てない)。canSetBackground=false では呼ばれない。
   */
  readonly onToggleBackgroundColorWheel: () => void;
  /**
   * レイヤーのサムネ (layer-thumbnails-2026-10-13.md)。番号バッジが届いたサムネに置き換わる。
   * null / 未指定は番号だけ。スマホのシートも同じパネルを再ホストするので、シートが
   * 開いている間だけ登録される (= 閉じている間は visible=false)。
   */
  readonly thumbnails?: LayerThumbnailHub | null;
  /** 範囲を浮かせている間だけ非 null。行に「置き先」「取った元」を添える (選択は変えない)。 */
  readonly regionRoles?: RegionLayerRoles | null;
}

export const ModernLayerPanel = ({
  layerCount,
  activeLayerId,
  visibility,
  onSelectActive,
  onToggleVisible,
  opacities = [],
  opacityControl = null,
  backgroundVisible,
  onToggleBackgroundVisible,
  backgroundRgb,
  canSetBackground,
  onToggleBackgroundColorWheel,
  thumbnails = null,
  regionRoles = null,
}: ModernLayerPanelProps): React.ReactNode => {
  const rows = buildLayerRows(layerCount);

  return (
    <section
      className="flex flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)]"
      aria-label="レイヤー"
      data-manual="layer.overview"
    >
      <ul className="flex flex-col p-2">
        {rows.map((row) => {
          const visible = visibility[row.id] ?? true;
          const active = row.id === activeLayerId;
          return (
            // biome-ignore lint/a11y/useKeyWithClickEvents: 行クリックはマウス/タッチ用の拡張。キーボードは行内の選択ボタン (aria-pressed) が担う
            <li
              key={row.id}
              data-testid={`modern-layer-row-${row.id}`}
              onClick={(e) => {
                if (shouldSelectOnRowClick(e)) onSelectActive(row.id);
              }}
              className={`relative flex cursor-pointer items-center gap-1 rounded transition-colors ${
                active ? 'bg-[var(--color-accent)]/20' : 'hover:bg-[var(--color-bg-soft)]/50'
              }`}
            >
              {/* 表示/非表示トグル (ローカル)。 */}
              <button
                type="button"
                aria-label={`${row.label}を${visible ? '非表示' : '表示'}`}
                aria-pressed={visible}
                data-manual="layer.visibility"
                onClick={() => onToggleVisible(row.id, !visible)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-soft)]"
              >
                {visible ? <EyeIcon className="h-4 w-4" /> : <EyeOffIcon className="h-4 w-4" />}
              </button>
              {/* 番号バッジ = キーボード用の選択ボタン (行全体も選択対象。つまみ・👁 は除く)。 */}
              <LayerThumb
                layerId={row.id}
                hub={thumbnails}
                active={active}
                ariaLabel={`${row.label}を選択`}
                dataManual="layer.select"
                testId={`modern-layer-select-${row.id}`}
                onSelect={() => onSelectActive(row.id)}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded border text-[12px] font-bold transition-colors ${
                  active
                    ? 'border-[var(--color-accent)] text-white'
                    : 'border-transparent text-[var(--color-text-muted)] hover:bg-[var(--color-bg-soft)]'
                } ${visible ? '' : 'opacity-50'}`}
              >
                {row.id + 1}
              </LayerThumb>
              {/* 常設うすさスライダ (自分の画面だけ)。つまみを掴んでいる間だけ動く。 */}
              <LayerOpacitySlider
                layerId={row.id}
                label={row.label}
                opacity={opacities[row.id] ?? 1}
                control={opacityControl}
                dimmedLook={!visible}
                testId={`modern-layer-opacity-${row.id}`}
              />
              <RegionRoleTag roles={regionRoles} layerId={row.id} />
            </li>
          );
        })}

        {/* 背景 疑似行 (2026-06-20 canvas-background-color §3.2/§3.3)。rows.map() の外
            = 本物 layer 配列に混ぜない (鉄則: 背景は Layer ではない)。列の最下段、
            選択不可、active 予約色とは別の沈んだトーン (--color-bg-inset)、layer 群
            との境界に区切り線。挙動は Classic LayerPopup と一致 (engine state 共通)。
            触れるのは 👁 のみ (■ swatch = 色変更 A は別 PR)。 */}
        <li className="mt-1 flex items-center gap-1 border-t border-black pt-1">
          {/* 表示/非表示トグル (ローカル、broadcast しない)。 */}
          <button
            type="button"
            aria-label={`背景を${backgroundVisible ? '非表示' : '表示'}`}
            aria-pressed={backgroundVisible}
            onClick={() => onToggleBackgroundVisible(!backgroundVisible)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-soft)]"
          >
            {backgroundVisible ? (
              <EyeIcon className="h-4 w-4" />
            ) : (
              <EyeOffIcon className="h-4 w-4" />
            )}
          </button>
          {/* 非インタラクティブなラベル (= active に選べない)。button ではなく span、
              hover ハイライト無し、cursor-default、--color-bg-inset の沈んだ地色。 */}
          <span
            className={`flex min-w-0 flex-1 cursor-default items-center rounded border border-transparent bg-[var(--color-bg-inset)] px-2 py-1 text-left text-[11px] text-[var(--color-text-muted)] ${
              backgroundVisible ? '' : 'opacity-50'
            }`}
          >
            <span className="truncate">背景</span>
          </span>

          {/* ■ swatch (A-4, canvas-background-color §3.1/§4.2)。現在背景色を常に表示
              (read-only chip 兼)。オーナー (canSetBackground) は click で背景用
              ColorWheelPalette を開く。非オーナーは disabled + グレーアウト + tooltip。 */}
          <button
            type="button"
            data-testid="modern-layer-panel-background-swatch"
            aria-label={
              canSetBackground
                ? `背景色 ${rgbToCss(backgroundRgb)} (クリックで変更)`
                : '背景色 (変更には権限が必要)'
            }
            title={canSetBackground ? '背景色を変更' : '背景色変更の権限がありません'}
            disabled={!canSetBackground}
            data-manual="color.background"
            onClick={() => {
              if (!canSetBackground) return;
              onToggleBackgroundColorWheel();
            }}
            className={`h-5 w-5 shrink-0 rounded border border-[var(--color-border)] ${
              canSetBackground ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'
            }`}
            style={{ backgroundColor: rgbToCss(backgroundRgb) }}
          />
        </li>
      </ul>
    </section>
  );
};
